"use client";

import { Button } from "@cmp-src/primitives/button";
import type {
  AiChatToolShowReportOutput,
  AiChatToolShowReportPart,
  ReportCatalog,
  ReportQuery
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useState } from "react";
import { FloorPicker } from "@/features/reporting/components/FloorPicker";
import { ReportMapView } from "@/features/reporting/components/ReportMapView";
import { ResultChart } from "@/features/reporting/components/ResultChart";
import {
  MAP_MEASURES,
  queryFloor,
  queryOffices,
  withFloor
} from "@/features/reporting/components/ResultPanel";
import { ResultTable } from "@/features/reporting/components/ResultTable";
import { SaveToBoardDialog } from "@/features/reporting/components/SaveToBoardDialog";
import { usePairedResult } from "@/features/reporting/hooks/usePairedResult";
import { useReportCatalog } from "@/features/reporting/hooks/useReportCatalog";
import { useReportQuery } from "@/features/reporting/hooks/useReportQuery";
import {
  describeQuery,
  lowerFirst
} from "@/features/reporting/lib/describeQuery";
import { formatDelta, formatValue } from "@/features/reporting/lib/formatValue";
import { chartFormOf, pickForm } from "@/features/reporting/lib/pickForm";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import type { TessaMessageRendererProps } from "@/features/tessa/types";

/** Stands in for the catalog until it arrives; nothing pairs against it. */
const NO_CATALOG: ReportCatalog = {
  measures: [],
  dimensions: [],
  datasets: [],
  periods: [],
  grains: [],
  compares: []
};

/** Tessa's query is loosely typed JSON; only a measure and a period make it runnable. */
const toReportQuery = (
  output: AiChatToolShowReportOutput | undefined
): ReportQuery | null => {
  const q = output?.query;
  if (!q?.measure || !q.period?.preset) {
    return null;
  }
  return {
    measure: q.measure,
    breakdown: q.breakdown ?? [],
    filters: q.filters ?? [],
    period: {
      preset: q.period.preset as ReportQuery["period"]["preset"],
      from: q.period.from || undefined,
      to: q.period.to || undefined,
      grain: (q.period.grain || undefined) as ReportQuery["period"]["grain"]
    },
    compare:
      q.compare === "previous_period" || q.compare === "same_period_last_year"
        ? q.compare
        : undefined
  };
};

type CardView = "chart" | "table" | "map";

/** A question about desk access reads best as the map of who may book each desk. */
const isAboutAccess = (query: ReportQuery): boolean =>
  (query.breakdown ?? []).includes("access_mode") ||
  (query.filters ?? []).some((f) => f.dimension === "access_mode");

interface ReportCardBodyProps {
  query: ReportQuery;
  /** Tessa's choice: chart, table, or map. */
  form: string;
  /** A second measure Tessa set against the first ("filed vs resolved"). */
  pairedWith?: string;
}

const ReportCardBody: React.FC<ReportCardBodyProps> = ({
  query,
  form,
  pairedWith
}) => {
  const { data: catalog } = useReportCatalog();
  const { data: result, isError } = useReportQuery(query);
  const pair = usePairedResult(
    catalog ?? NO_CATALOG,
    query,
    pairedWith,
    result
  );
  const canMap = MAP_MEASURES.has(query.measure);
  const [view, setView] = useState<CardView>(
    form === "map" && canMap ? "map" : form === "table" ? "table" : "chart"
  );
  // Tessa's answer may ask for a map without naming a floor; the reader
  // picks one here and the map, and a saved tile, follow it.
  const [floorId, setFloorId] = useState<string | null>(queryFloor(query));
  const [isSaveOpen, setIsSaveOpen] = useState(false);

  if (isError || pair.isError) {
    return (
      <p className="text-[12px] text-semantic-error-foreground">
        Couldn't run this report.
      </p>
    );
  }
  if (!catalog || !result || pair.isLoading) {
    return (
      <p className="animate-pulse text-[12px] text-neutral-400">Loading…</p>
    );
  }

  const measure = result.meta.measure;
  const paired = pair.paired;
  // A pair is its own comparison; the previous period stays out.
  const delta = paired
    ? null
    : formatDelta(measure, result.totals.value, result.totals.previous);
  const chartForm = pickForm(query, "auto", !!paired);
  const isMap = view === "map";
  const mapQuery = floorId ? withFloor(query, floorId) : query;
  const views: { key: CardView; label: string }[] = [
    { key: "chart", label: "Chart" },
    { key: "table", label: "Table" },
    ...(canMap ? [{ key: "map" as const, label: "Map" }] : [])
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[12px] text-neutral-500">
            {describeQuery(catalog, query, paired?.measure.key)}
          </span>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[26px] text-neutral-800 tabular-nums leading-[30px]">
              {formatValue(measure, result.totals.value)}
            </span>
            {paired ? (
              <span className="text-[12px] text-neutral-500 tabular-nums">
                {`vs ${formatValue(measure, paired.result.totals.value)} ${lowerFirst(paired.measure.label)}`}
              </span>
            ) : null}
            {delta ? (
              <span
                className={cn(
                  "text-[12px] tabular-nums",
                  delta.isFlat
                    ? "text-neutral-500"
                    : delta.isGood
                      ? "text-[#006300]"
                      : "text-semantic-error-foreground"
                )}
              >
                {delta.text}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {views.map((option) => (
            <Button
              key={option.key}
              variant={view === option.key ? "default" : "outline"}
              size="sm"
              onClick={() => setView(option.key)}
            >
              {option.label}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSaveOpen(true)}
          >
            Add to board
          </Button>
        </div>
      </div>
      {isMap ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {floorId ? null : (
              <span className="text-[12px] text-neutral-500">
                Pick a floor to draw this on its plan.
              </span>
            )}
            <FloorPicker
              catalog={catalog}
              floorId={floorId}
              officeIds={queryOffices(query)}
              onPick={setFloorId}
            />
          </div>
          {floorId ? (
            <ReportMapView
              catalog={catalog}
              measure={query.measure}
              floorId={floorId}
              period={query.period}
              height={360}
              initialMode={isAboutAccess(query) ? "assignments" : "period"}
            />
          ) : null}
        </div>
      ) : view === "table" || chartForm === "table" || chartForm === "map" ? (
        <ResultTable catalog={catalog} result={result} paired={paired} />
      ) : (
        <ResultChart
          form={chartFormOf(chartForm)}
          result={result}
          query={query}
          paired={paired}
          onDrill={() => undefined}
        />
      )}
      <SaveToBoardDialog
        catalog={catalog}
        draft={{
          // The tile shows what the card shows: a map with its floor, a table, or a chart.
          view: isMap && floorId ? "map" : view === "table" ? "table" : "chart",
          query: isMap && floorId ? mapQuery : query,
          records: null,
          // Only a chart tile draws a pair.
          chart:
            paired && !(isMap && floorId) && view !== "table"
              ? { with: paired.measure.key }
              : null
        }}
        isOpen={isSaveOpen}
        onClose={() => setIsSaveOpen(false)}
      />
    </div>
  );
};

/** The `report_result` card: runs Tessa's query client-side and draws it. */
export const ChatBotToolShowReport: React.FC<
  TessaMessageRendererProps<AiChatToolShowReportPart>
> = ({ part }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowReportOutput
    | undefined;
  const query = toReportQuery(output);

  return (
    <ChatBotToolCard
      className="w-full max-w-[720px]"
      isThinking={!query}
      toolId={part?.toolCallId}
    >
      {query ? (
        <ReportCardBody
          query={query}
          form={output?.form ?? "chart"}
          pairedWith={output?.with || undefined}
        />
      ) : null}
    </ChatBotToolCard>
  );
};
